/**
 * The theme configuration model (Phase 0 deliverable, roadmap 01 "Theme
 * configuration"). One typed object describes a brand: names, logo variants,
 * palettes as scales, semantic colors, font stacks + how to load them, radius
 * and spacing scales, type scale, and two display modes (light / dark) whose
 * resolved tokens the `ThemeProvider` emits as CSS variables.
 *
 * Nothing outside `theme/` may reference a brand value directly: components read
 * `var(--color-*)`, `var(--font-*)`, `var(--radius-*)`, `var(--space-*)` only.
 *
 * Validated with zod (the tree-shakeable `zod/mini` build) at module load so a
 * malformed theme fails fast in dev/tests rather than rendering with missing
 * variables.
 */
import * as z from "zod/mini";

const hex = z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/, "expected a 6-digit hex color like #D9A657"));

/** Any CSS <color> — used where translucent rgba() tints are intentional. */
const cssColor = z.string().check(z.minLength(1));

/** A URL (https or data:) or root-relative path to a brand asset. */
const assetUrl = z
  .string()
  .check(z.regex(/^(https?:\/\/|data:image\/|\/)/, "expected an https://, data:image/ or root-relative URL"));

const nonEmpty = z.string().check(z.minLength(1));

export const SemanticColorSchema = z.object({
  /** Text / icon color that passes AA on the mode's surface. */
  fg: hex,
  /** Soft tinted background for badges and callouts. */
  bg: cssColor,
  /** Border for badges and callouts. */
  border: cssColor,
});
export type SemanticColor = z.infer<typeof SemanticColorSchema>;

export const ModeTokensSchema = z.object({
  colorScheme: z.enum(["light", "dark"]),
  /** Page background. */
  bg: hex,
  /** Cards, panels, table bodies. */
  surface: hex,
  /** Popovers, menus, sheets — one step above `surface`. */
  surfaceRaised: hex,
  /** Inset wells (code, QR boxes, table headers). */
  surfaceSunken: hex,
  border: hex,
  borderStrong: hex,
  text: hex,
  textSecondary: hex,
  textMuted: hex,
  /** Disabled / decorative only — not guaranteed to pass AA. */
  textFaint: hex,
  /** The single brand accent: fills, borders, icons. */
  accent: hex,
  accentHover: hex,
  /** Text color placed ON an accent fill (dark text on gold). */
  accentFg: hex,
  accentSoft: cssColor,
  accentBorder: cssColor,
  /** Accent shade allowed for LARGE display figures on this mode's surfaces (≥ 3:1). */
  accentFigure: hex,
  /** Uppercase, letter-spaced labels ("eyebrows"). Must pass AA for small text. */
  eyebrow: hex,
  /** Text links — must pass AA (≥ 4.5:1) on `surface` and `bg`. */
  link: hex,
  linkHover: hex,
  focusRing: cssColor,
  rowHover: cssColor,
  overlay: cssColor,
  semantic: z.object({
    success: SemanticColorSchema,
    warning: SemanticColorSchema,
    danger: SemanticColorSchema,
    info: SemanticColorSchema,
  }),
  shadowSm: z.string(),
  shadowMd: z.string(),
});
export type ModeTokens = z.infer<typeof ModeTokensSchema>;

const scaleKey = z.string().check(z.regex(/^\d+$/));
const scale = z.record(scaleKey, hex);

export const FontFaceSchema = z.object({
  /** Web font family name as declared by the provider, or null for a system stack. */
  family: z.nullable(z.string()),
  /** Full CSS `font-family` stack including system fallbacks. */
  stack: nonEmpty,
});
export type FontFace = z.infer<typeof FontFaceSchema>;

export const ThemeSchema = z.object({
  /** Machine name selected by `/api/config.theme` / `VITE_THEME`. */
  name: z.string().check(z.regex(/^[a-z][a-z0-9-]*$/)),
  productName: nonEmpty,
  shortName: nonEmpty,
  legalName: nonEmpty,
  tagline: z.optional(z.string()),
  supportEmail: z.optional(z.email()),
  logo: z.object({
    /** Lockup (mark + wordmark) for DARK surfaces, or null if not published. */
    lockupDark: z.nullable(assetUrl),
    /** Lockup for LIGHT surfaces, or null → render `mark` + a text wordmark. */
    lockupLight: z.nullable(assetUrl),
    mark: assetUrl,
    favicon: assetUrl,
    touchIcon: z.optional(assetUrl),
    /** `<meta name="theme-color">` for the browser UI. */
    themeColor: hex,
  }),
  palette: z.object({
    /** Brand accent scale, lightest → darkest. */
    primary: scale,
    /** Warm/cool neutral scale, lightest → darkest. */
    neutral: scale,
  }),
  fonts: z.object({
    display: FontFaceSchema,
    body: FontFaceSchema,
    mono: FontFaceSchema,
    /** A stylesheet `<link href>` that loads the web fonts (Google Fonts), or null for system-only stacks. */
    stylesheetHref: z.nullable(z.url()),
  }),
  type: z.object({
    /** Body copy size, e.g. "15px". */
    body: z.string(),
    xs: z.string(),
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
    xl: z.string(),
    "2xl": z.string(),
    "3xl": z.string(),
    display: z.string(),
    leadingBody: z.string(),
    leadingTight: z.string(),
    /** Letter-spacing for eyebrows / table headers. */
    trackingEyebrow: z.string(),
    /** `uppercase` or `none` — brand voice for labels. */
    eyebrowTransform: z.enum(["uppercase", "none"]),
  }),
  radius: z.object({
    sm: z.string(),
    md: z.string(),
    lg: z.string(),
    pill: z.string(),
  }),
  spacing: z.record(scaleKey, z.string()),
  modes: z.object({
    light: ModeTokensSchema,
    dark: ModeTokensSchema,
  }),
  email: z.object({
    senderDisplayName: nonEmpty,
  }),
});

export type Theme = z.infer<typeof ThemeSchema>;
export type ThemeMode = "light" | "dark";
/** What the user asked for; `system` follows `prefers-color-scheme`. */
export type ModePreference = ThemeMode | "system";

/** Parse + validate a theme object; throws a descriptive ZodError when invalid. */
export function defineTheme(input: Theme): Theme {
  return z.parse(ThemeSchema, input);
}
